import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { SidepanelLocksListTypes, Lock } from './types';
import useQueryState from '../table/hooks/use-query-state';
import usePrevious from '../../hooks/use-previous';
import useGetList from '../../hooks/use-get-list';
import useFilters from '../table/hooks/use-filters';
import formatLocksList from './helpers/format-locks-list';
import useScroll from '../../hooks/use-scroll';
import { SelectItemProps } from '../select/types';
import { reasons } from './variables/lists';
import { PermissionEnum } from '../../base-types';
import { Filters, SchemaFilterType } from '../table/types';
import IconLoader from '../icons/loader';
import Table from '../table/table';
import ButtonPrimary from '../button/button-primary';
import { ButtonSize } from '../button/types';
import IconExpand from '../icons/expand';

import cn from './styles.module.css';

export default function SidepanelLocksList({
  users,
  locks,
  locksPagination,
  userId,
  getList,
  schema,
  filtersSchema,
  setLockId,
  onBlockUserClick,
  isFetching,
  activePermissions,
  panelIsOpen,
  isLoading,
  columns,
  columnsLabels,
  listClassName = '',
  withoutChangeSearchParams,
}: SidepanelLocksListTypes) {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    schema,
    withoutChangeSearchParams,
  );

  const prevFilters = usePrevious(filters);

  const { search } = useLocation();

  const { getListAction, setPage, onFilter, errorList } = useGetList<Lock>({
    getList,
    onFilterChange,
    onSortChange,
    schema,
    withoutChangeSearchParams,
  });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatLocksList(locks as Lock[], users);

    return data;
  }, [locks, sort, filters, users]);

  const onRowClick = (id: string) => {
    setLockId(id);
    onBlockUserClick();
  };

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    setValues(
      'createdByEmployeeId',
      users?.map((item) => ({
        name: `${item?.profile?.firstName} ${item?.profile?.lastName}`,
        value: item.id,
      })) as SelectItemProps[],
    );

    setValues('reason', reasons);
  }, [users]);

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !locks &&
      userId &&
      activePermissions?.includes(PermissionEnum.LOCKS_LIST) &&
      panelIsOpen
    ) {
      const nextFilters: Filters = {
        ...filters,
        userId: {
          value: [userId],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: nextFilters,
      });
    }
  }, [
    filters,
    prevFilters,
    locks,
    userId,
    search,
    activePermissions,
    panelIsOpen,
  ]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.locksLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        columns={columns}
        data={formattedData || []}
        onSort={() => {}}
        filters={filters}
        sort={sort}
        columnsLabels={columnsLabels}
        tableTopClassName={cn.userLocksTableTop}
        onFilterChange={(filterValues) => {
          const nextFilters: Filters = {
            ...filterValues,
            userId: {
              value: [userId as string],
              type: SchemaFilterType.STRING,
            },
          };

          onFilter({
            ...nextFilters,
          });
        }}
        filtersComponent={filtersComponent}
        handleSubmit={handleSubmit}
        isLoading={isLoading || isFetching}
        onRowClick={onRowClick}
        take="20"
        setPage={(data) => {
          const filterValues: Filters = {
            ...filters,
            userId: {
              value: [userId as string],
              type: SchemaFilterType.STRING,
            },
          };

          setPage({ ...data, filters: filterValues });
        }}
        topContent={
          <div>
            {activePermissions?.includes(
              PermissionEnum.LOCKS_CREATE_ACTION,
            ) && (
              <ButtonPrimary
                size={ButtonSize.SMALL}
                text="Block a user"
                icon={<IconExpand />}
                onClick={onBlockUserClick}
              />
            )}
          </div>
        }
        pagination={locksPagination}
        error={errorList}
        withVerticalScroll
        withScroll
      />
    );
  }

  return (
    <div
      className={`${cn.securityLocksContent} ${listClassName}`}
      ref={scrollRef}
    >
      {content}
    </div>
  );
}
